import React from 'react';
import { faCodeBranch } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/context-graphs-confusion.jpg
import contextGraphsConfusionImg from '../../../assets/images/blogs/State-Machines-vs-Workflows.gif';

const ContextGraphsConfusion = () => (
    <BlogPost
        title="Context Graphs: A Tangled Web Of Confusion"
        subtitle="State Machines, Workflows, and Agents - understanding the fundamental problem space."
        category="Architecture"
        categoryIcon={faCodeBranch}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={contextGraphsConfusionImg}
        imageAlt="Context graphs - understanding state machines versus workflows"
        ctaHeading="Need clarity on context graphs?"
        ctaText="Book a discovery call and we'll show you how AgentDOG uses state machines for dynamic agent navigation."
        seoDescription="Clarifying the confusion around context graphs by explaining the fundamental difference between state machines and workflows in agentic systems."
        seoKeywords="context graphs, state machines, workflows, agentic AI, decision spaces, Nebulyx"
        slug="context-graphs-confusion"
    >
        <p>
            With Context Graphs, I'm having loads of great discussions (with a lot of
            awesome critique and feedback - love it, helps me learn). 🙏
        </p>

        <p>
            But also I see a lot trying to mis-classify the problem space by overlaying
            other graph-based solutions onto this one.
        </p>

        <p>
            There seems to be confusion about the space we are operating in.
        </p>

        <p>
            So, with that in mind, I thought I'd go back to basics.
        </p>

        <h3>State Machines - Concepts</h3>

        <p>
            <strong>States</strong> are where you are and where you can possibly move to.
            <strong>Transitions</strong> are how you move. <strong>Events</strong> are
            what triggers movement. <strong>Guards</strong> are conditions that allow
            or block.
        </p>

        <p>
            The system is always in one state. They have a possibility space - i.e. from
            the current position (state), various options exist (some known and some
            unknown - this is key), but the path between them isn't predefined.
        </p>

        <p>
            Using the information available (context) a decision is made, resulting in a
            move to a different state.
        </p>

        <blockquote className="blog-post-quote">
            "What happens next determines where you go - GPS dynamic re-routing vs routes
            marked on a Map."
        </blockquote>

        <p>
            The path emerges from execution but isn't known ahead of time.
        </p>

        <h3>Workflows vs State Machines</h3>

        <p>
            <strong>Workflows</strong> (including procedural knowledge approaches) are
            predefined sequences of steps - the path is predefined and decreed upfront.
            E.g. Step 1 → Step 2 → Step 3.
        </p>

        <p>
            All options are not only known but the sequence is typically defined upfront
            (although can evolve - but not typically at runtime).
        </p>

        <p>
            Note: trying to make workflows dynamic is trying to fit a square peg in a
            round hole - probably can engineer, but it's the wrong pattern.
        </p>

        <p>
            Both valid but very different behaviours for different problem spaces!
        </p>

        <p>
            🤖 <strong>Agent → Uses State Machines</strong>
        </p>

        <p>
            🔧 <strong>Traditional automation → Typically uses workflows</strong>
        </p>

        <h3>How Agents Navigate State Machines</h3>

        <p>
            They observe state (current goal or state in its execution process), evaluate
            options and choose the next state based on context (all info needed to make
            the decision), then execute and arrive at new state.
        </p>

        <p>
            <strong>Agents also operate in an unknown state space</strong> - i.e. they
            can create new options as they go. This fundamentally breaks predefined flow
            approaches. 💡
        </p>

        <p>
            This repeats. The path isn't scripted - it's discovered at runtime.
        </p>

        <h3>Context Graphs</h3>

        <p>
            A context graph is the structure an agent navigates:
        </p>

        <p>
            Current state. Available transitions. Execution history. Plus all the decision
            and other information used to make the decision to the appropriate transition
            (judgment, decision criteria etc - the "context" used).
        </p>

        <p>
            It's live at runtime and persisted as decision trace when complete - with the
            accumulated traces becoming organisational memory.
        </p>

        <h3>Why The Confusion?</h3>

        <p>
            Context graphs sit at the intersection of data, software, and AI. Each
            discipline sees it through their own lens.
        </p>

        <p>
            We can definitely debate if the graph is just a log (the FC Capital POV) or
            also moves into the actual execution (what we've built) - but first we must
            acknowledge the fundamental problem space we are in.
        </p>

        <p>
            The terminology will sort itself out. ✌️ 🎯
        </p>
    </BlogPost>
);

export default ContextGraphsConfusion;
